import React from 'react';
import { BookImage, Globe, Shield } from 'lucide-react';
import type { ContentSection } from '../../api/insights';
import shared from './shared.module.css';
import styles from './ContentLibraryGroup.module.css';

interface Props {
  data: ContentSection;
}

export function ContentLibraryGroup({ data }: Props) {
  const totalAlbums = data.albums.regular + data.albums.live + data.albums.slideshowEnabled;
  const totalGalleries = data.publicGalleries.active + data.publicGalleries.expired;

  return (
    <div className={shared['group-card']}>
      <div className={shared['group-header']}>
        <div className={shared['group-icon']}><BookImage size={15} /></div>
        <div>
          <div className={shared['group-title']}>Content Library &amp; Sharing</div>
          <div className={shared['group-subtitle']}>What's published and how it's accessible</div>
        </div>
      </div>

      {/* Events + albums */}
      <div className={shared['stat-row']}>
        <div className={shared['stat-card']}>
          <span className={shared['stat-label']}>Active events</span>
          <span className={`${shared['stat-value']} ${shared['stat-value--green']}`}>{data.events.active}</span>
        </div>
        <div className={shared['stat-card']}>
          <span className={shared['stat-label']}>Ended events</span>
          <span className={shared['stat-value']}>{data.events.ended}</span>
        </div>
        <div className={shared['stat-card']}>
          <span className={shared['stat-label']}>Total photos</span>
          <span className={shared['stat-value']}>{data.totalPhotos.toLocaleString()}</span>
        </div>
        <div className={shared['stat-card']}>
          <span className={shared['stat-label']}>Export presets</span>
          <span className={shared['stat-value']}>{data.exportPresets}</span>
        </div>
      </div>

      {/* Album type breakdown */}
      {totalAlbums > 0 && (
        <div>
          <div className={shared['section-label']}>Albums ({totalAlbums} total)</div>
          <div className={styles['album-chips']}>
            <span className={`${shared.badge} ${shared['badge--neutral']}`}>{data.albums.regular} Regular</span>
            <span className={`${shared.badge} ${shared['badge--red']}`}>{data.albums.live} Live</span>
            <span className={`${shared.badge} ${shared['badge--neutral']}`}>{data.albums.slideshowEnabled} Slideshow</span>
          </div>
        </div>
      )}

      {/* Public galleries */}
      {totalGalleries > 0 && (
        <div>
          <div className={shared['section-label']}>Public gallery links</div>
          <div className={styles['gallery-grid']}>
            <div className={shared['stat-card']}>
              <span className={shared['stat-label']}><Globe size={10} style={{ verticalAlign: 'middle', marginRight: 3 }} />Active</span>
              <span className={`${shared['stat-value']} ${shared['stat-value--green']}`}>{data.publicGalleries.active}</span>
            </div>
            <div className={shared['stat-card']}>
              <span className={shared['stat-label']}>Expired</span>
              <span className={shared['stat-value']}>{data.publicGalleries.expired}</span>
            </div>
            <div className={shared['stat-card']}>
              <span className={shared['stat-label']}><Shield size={10} style={{ verticalAlign: 'middle', marginRight: 3 }} />Password protected</span>
              <span className={shared['stat-value']}>{data.publicGalleries.passwordProtected}</span>
            </div>
            <div className={shared['stat-card']}>
              <span className={shared['stat-label']}>Open access</span>
              <span className={shared['stat-value']}>{data.publicGalleries.open}</span>
            </div>
          </div>
        </div>
      )}

      {/* Overlays per event */}
      {data.overlaysPerEvent.length > 0 && (
        <div>
          <div className={shared['section-label']}>Overlays per event</div>
          <div className={shared['ranked-list']}>
            {data.overlaysPerEvent.map((o) => (
              <div key={o.eventId} className={shared['ranked-item']}>
                <span className={shared['ranked-label']}>{o.eventName}</span>
                <span className={`${shared.badge} ${shared['badge--neutral']}`}>{o.overlayCount}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
